import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getMeals, FOOD_IMAGES, getRealLatLng } from '../utils/storage';
import './MapViewPage.css';

// Fix Leaflet marker default assets
const DefaultIcon = L.icon({
  iconUrl: new URL('../../node_modules/leaflet/dist/images/marker-icon.png', import.meta.url).href,
  iconRetinaUrl: new URL('../../node_modules/leaflet/dist/images/marker-icon-2x.png', import.meta.url).href,
  shadowUrl: new URL('../../node_modules/leaflet/dist/images/marker-shadow.png', import.meta.url).href,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

// Custom div icon builder for meal markers
const createMealIcon = (photoUrl: string | undefined, foodType: string) => {
  const imgUrl = photoUrl || FOOD_IMAGES[foodType] || FOOD_IMAGES.apple;
  return L.divIcon({
    className: 'custom-leaflet-meal-marker',
    html: `
      <div class="leaflet-marker-pin-body">
        <img src="${imgUrl}" class="leaflet-marker-food-img" />
      </div>
      <div class="leaflet-marker-pin-shadow"></div>
    `,
    iconSize: [44, 44],
    iconAnchor: [22, 44],
    popupAnchor: [0, -44]
  });
};

export function MapViewPage() {
  const navigate = useNavigate();
  const meals = getMeals();

  // Dynamic map center: defaults to Harvard Yard, queries geolocation if no meals exist
  const [mapCenter, setMapCenter] = React.useState<[number, number]>([42.3736, -71.1186]);

  React.useEffect(() => {
    if (meals.length > 0) {
      const lastMeal = meals[meals.length - 1];
      setMapCenter(getRealLatLng(lastMeal.gps.lat, lastMeal.gps.lng));
    } else if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setMapCenter([position.coords.latitude, position.coords.longitude]);
        },
        (error) => {
          console.warn('Geolocation denied/error on MapViewPage:', error);
        }
      );
    }
  }, [meals]);

  return (
    <div className="map-view-container">
      <div className="map-header">
        <h2>Meal Map</h2>
        <p className="map-subtitle">Locations of your logged dining experiences</p>
      </div>

      <div className="nook-island-map-leaflet-wrapper">
        <MapContainer 
          key={`${mapCenter[0]}-${mapCenter[1]}`}
          center={mapCenter} 
          zoom={15} 
          style={{ width: '100%', height: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {meals.map((meal) => {
            const position = getRealLatLng(meal.gps.lat, meal.gps.lng);
            return (
              <Marker
                key={meal.id}
                position={position}
                icon={createMealIcon(meal.photoUrl, meal.foodType)}
              >
                <Popup className="nook-leaflet-popup">
                  <div className="popup-card-content-leaflet">
                    <img
                      src={meal.photoUrl || FOOD_IMAGES[meal.foodType] || FOOD_IMAGES.apple}
                      alt={meal.foodType}
                      className="popup-meal-img-leaflet"
                    />
                    <div className="popup-card-mid-leaflet">
                      <div className="popup-meal-time-leaflet">🕒 {meal.timestamp}</div>
                      <div className="popup-meal-name-leaflet">{meal.customName}</div>
                      <div className="popup-meal-loc-leaflet">📍 {meal.gps.locationName}</div>
                    </div>
                    <button
                      className="popup-view-btn-leaflet"
                      onClick={() => navigate(`/meal/${meal.id}`)}
                    >
                      View Details ➔
                    </button>
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>
    </div>
  );
}

export default MapViewPage;
